import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCart } from '@/contexts/CartContext';
import { urlFor } from '@/utils/sanityUtil';
import { sendCartToWhatsAPP } from '@/utils/whatsappUtil';

interface MiniCartProps {
  isOpen: boolean;
  onClose: () => void;
}

const MiniCart = ({ isOpen, onClose }: MiniCartProps) => {
  const router = useRouter();
  const { state, removeItem, updateQuantity, getTotalPrice, clearCart } = useCart();
  const [isOrdering, setIsOrdering] = useState(false);

  const handleOrderCart = async () => {
    if (state.items.length === 0) return;
    
    setIsOrdering(true);
    try {
      sendCartToWhatsAPP(router, state.items);
      // Optional: Clear cart after successful order
      // clearCart();
      onClose();
    } catch (error) {
      console.error('Error sending order:', error);
    } finally {
      setIsOrdering(false);
    }
  };

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="mini-cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Cart Panel */}
          <motion.div
            className="mini-cart"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <div className="mini-cart__header">
              <h3 className="mini-cart__title">Shopping Cart</h3>
              <button className="mini-cart__close" onClick={onClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="mini-cart__content">
              {state.items.length === 0 ? (
                <div className="mini-cart__empty">
                  <div className="mini-cart__empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 16.1 19 15 19H9C7.9 19 7 18.1 7 17V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className="mini-cart__empty-text">Your cart is empty</p>
                  <Link href="/shop" className="mini-cart__shop-link" onClick={onClose}>
                    Continue Shopping
                  </Link>
                </div>
              ) : (
                <>
                  <div className="mini-cart__items">
                    {state.items.map((item) => (
                      <div key={item.id} className="mini-cart__item">
                        <div className="mini-cart__item-image">
                          <Image
                            src={typeof item.product.mainImage === 'string' 
                              ? item.product.mainImage 
                              : urlFor(item.product.mainImage)}
                            alt={item.product.title}
                            width={60}
                            height={60}
                            className="mini-cart__item-img"
                          />
                        </div>
                        
                        <div className="mini-cart__item-details">
                          <h4 className="mini-cart__item-title">{item.product.title}</h4>
                          <p className="mini-cart__item-variant">{item.variant.title}</p>
                          <p className="mini-cart__item-price">
                            LKR {item.variant.price.toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="mini-cart__item-controls">
                          <div className="mini-cart__quantity">
                            <button
                              className="mini-cart__quantity-btn"
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            >
                              -
                            </button>
                            <span className="mini-cart__quantity-value">{item.quantity}</span>
                            <button
                              className="mini-cart__quantity-btn"
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            >
                              +
                            </button>
                          </div>
                          
                          <button
                            className="mini-cart__remove"
                            onClick={() => removeItem(item.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6H5H21M8 6V4C8 3.4 8.4 3 9 3H15C15.6 3 16 3.4 16 4V6M19 6V20C19 20.6 18.6 21 18 21H6C5.4 21 5 20.6 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mini-cart__footer">
                    <div className="mini-cart__total">
                      <span className="mini-cart__total-label">Total:</span>
                      <span className="mini-cart__total-amount">
                        LKR {getTotalPrice().toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="mini-cart__actions">
                      <Link href="/cart" className="mini-cart__view-cart" onClick={onClose}>
                        View Cart
                      </Link>
                      
                      <button
                        className="mini-cart__clear"
                        onClick={clearCart}
                        disabled={isOrdering}
                      >
                        Clear Cart
                      </button>
                      
                      <button
                        className="mini-cart__order"
                        onClick={handleOrderCart}
                        disabled={isOrdering}
                      >
                        {isOrdering ? 'Preparing...' : 'Order via WhatsApp'}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MiniCart;